import React from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../config/api';
import { Calendar, ArrowLeft } from 'lucide-react';
import { BRAND } from '../config/brand';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const StaticPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const handleGoBack = () => {
        if (window.history.length > 1) {
            navigate(-1); // Go back to previous page
        } else {
            navigate('/'); // Go to home if no history
        }
    };

    const { data: pageData, isLoading, error } = useQuery(
        ['static-page', slug],
        async () => {
            const response = await api.get(`/api/pages/${slug}`);
            return response.data;
        },
        {
            enabled: !!slug,
            staleTime: 10 * 60 * 1000, // 10 minutes
        }
    );

    if (isLoading) {

        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error || !pageData?.page) {
        return <Navigate to="/404" replace />;
    }

    const page = pageData.page;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            <SEO
                title={page.title}
                description={page.meta_description || BRAND.claimRu}
                canonical={`${BRAND.baseUrl}/pages/${encodeURIComponent(String(slug || ''))}`}
                url={`/pages/${encodeURIComponent(String(slug || ''))}`}
                type="article"
            />
            <div className="min-h-screen pages-ul bg-secondary-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    

                    <article className="dark:bg-secondary-900 rounded-2xl shadow-xl overflow-hidden border border-secondary-200">
                        {/* Header */}
                        <div className="px-8 py-10 bg-gradient-to-r from-primary-700 to-primary-500 text-white">
                            <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                                {page.title}
                            </h1>

                            {page.meta_description && (
                                <p className="text-xl text-white/90 mb-6 leading-relaxed">
                                    {page.meta_description}
                                </p>
                            )}

                            <div className="flex items-center text-white/80 space-x-6">
                                <div className="flex items-center">
                                    <Calendar className="w-5 h-5 mr-2" />
                                    <span>Обновлено: {formatDate(page.updated_at)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-8 py-12">
                            {page.content ? (
                                <div
                                    className="static-page-content prose prose-xl max-w-none
                  prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-gray-900
                  prose-h1:text-4xl prose-h1:mb-8 prose-h1:mt-12 prose-h1:pb-4 prose-h1:border-b prose-h1:border-secondary-200
                  prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-10 prose-h2:text-primary-700
                  prose-h3:text-2xl prose-h3:mb-5 prose-h3:mt-8 prose-h3:text-primary-600
                  prose-h4:text-xl prose-h4:mb-4 prose-h4:mt-6 prose-h4:text-primary-600
                  prose-h5:text-lg prose-h5:mb-3 prose-h5:mt-5 prose-h5:text-primary-500
                  prose-h6:text-base prose-h6:mb-3 prose-h6:mt-4 prose-h6:text-gray-600 prose-h6:font-semibold
                  prose-p:text-gray-700 prose-p:leading-8 prose-p:mb-6
                  prose-a:text-primary-600 prose-a:no-underline prose-a:font-medium hover:prose-a:text-primary-800 hover:prose-a:underline
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-em:text-gray-600 prose-em:italic
                  prose-ul:text-gray-700 prose-ul:mb-6
                  prose-ol:text-gray-700 prose-ol:mb-6
                  prose-li:text-gray-700 prose-li:mb-2 prose-li:leading-7
                  prose-blockquote:text-gray-600 prose-blockquote:border-primary-300 prose-blockquote:bg-primary-50 prose-blockquote:p-6 prose-blockquote:rounded-lg prose-blockquote:italic
                  prose-code:text-primary-700 prose-code:bg-primary-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:font-mono prose-code:text-sm
                  prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-6
                  prose-hr:border-gray-300 prose-hr:my-12
                  prose-table:border-collapse prose-table:w-full prose-table:mb-6
                  prose-th:border prose-th:border-gray-300 prose-th:bg-secondary-50 prose-th:p-3 prose-th:text-left prose-th:font-semibold
                  prose-td:border prose-td:border-gray-300 prose-td:p-3
                  prose-img:rounded-lg prose-img:shadow-lg prose-img:mx-auto prose-img:mb-8"
                                    dangerouslySetInnerHTML={{ __html: page.content }}
                                />
                            ) : (
                                <div className="text-center py-20">
                                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <Calendar className="w-8 h-8 text-primary-600" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Содержимое не добавлено</h3>
                                    <p className="text-gray-500">Эта страница пока не содержит контента.</p>
                                </div>
                            )}

                            {page.show_contact_form === true && (
                                <ContactForm pageSlug={page.slug || slug} />
                            )}
                        </div>
                    </article>
                </div>
            </div>
        </>
    );
};

export default StaticPage;
